/**
 * The Edit-tool press resolver (REQ-UI-136) — what a press *means*.
 *
 * The Edit tool serves every annotation kind with one palette entry, which
 * works only because a press is disambiguated by **what is under the cursor**
 * rather than by a mode. This module is that decision, kept pure and separate
 * from the pointer plumbing so the priority order is pinned by tests rather
 * than emerging from the order event handlers happen to run in.
 *
 * Priority, most specific first::
 *
 *     vertex handle  →  move that corner (click without moving = delete it)
 *     midpoint handle →  insert a corner there
 *     radius handle  →  resize the circle
 *     shape body     →  translate the whole shape
 *     nothing        →  no edit action
 *
 * Vertex beats midpoint because midpoints sit **on** the segment between two
 * vertices: near a short segment both are within threshold, and of the two
 * possible readings, "add a corner I did not ask for" is the one that silently
 * changes the shape. Moving an existing corner is the safer default.
 *
 * A handle also beats a body hit from a *different* annotation. Handles are the
 * more specific target and carry their own annotation id, so with overlapping
 * shapes the press edits the handle's shape rather than whatever is painted
 * underneath.
 *
 * Cross-references:
 *   - ADR-034 D7 (editing surface); feature-spec §8 (interaction model).
 *   - ./annotation-handles (the handles this resolves against).
 *   - @/map/use-annotation-drag (the hook that applies the resolved action).
 */
import type { HandleKind } from "@/annotations/annotation-handles";

/** A handle found under the cursor (read off the handle layer's features). */
export interface HandleHit {
  readonly annotationId: string;
  readonly kind: HandleKind;
  /** The vertex the handle addresses, or the segment start for a midpoint. */
  readonly vertexIndex: number;
}

/** What the press should do. */
export type EditAction =
  | { readonly type: "move-vertex"; readonly annotationId: string; readonly vertexIndex: number }
  | { readonly type: "insert-vertex"; readonly annotationId: string; readonly segmentIndex: number }
  | { readonly type: "resize-circle"; readonly annotationId: string }
  | { readonly type: "translate"; readonly annotationId: string };

/** What the cursor is over: any handles hit, plus the shape body beneath. */
export interface EditPressTarget {
  readonly handles: readonly HandleHit[];
  readonly bodyAnnotationId: string | null;
}

/** Handle kinds in resolution order — the priority the module documents. */
const HANDLE_PRIORITY: readonly HandleKind[] = ["vertex", "radius", "midpoint"];

/**
 * Resolve a press to the edit action it should perform, or ``null`` when there
 * is nothing editable under the cursor.
 */
export function resolveEditPress(target: EditPressTarget): EditAction | null {
  for (const kind of HANDLE_PRIORITY) {
    const hit = target.handles.find((handle) => handle.kind === kind);
    if (hit === undefined) {
      continue;
    }
    if (hit.kind === "vertex") {
      return { type: "move-vertex", annotationId: hit.annotationId, vertexIndex: hit.vertexIndex };
    }
    if (hit.kind === "radius") {
      return { type: "resize-circle", annotationId: hit.annotationId };
    }
    return { type: "insert-vertex", annotationId: hit.annotationId, segmentIndex: hit.vertexIndex };
  }
  if (target.bodyAnnotationId !== null) {
    return { type: "translate", annotationId: target.bodyAnnotationId };
  }
  return null;
}

/** How far the pointer may travel between press and release and still count as
 *  a click rather than a drag (screen px). */
const CLICK_SLOP_PX = 3;

/**
 * Whether a press-release on a vertex handle was a **click** (delete it) rather
 * than a drag (move it).
 *
 * The guard exists because deleting a vertex is destructive and ADR-034 D7
 * defers undo — the only recovery is redrawing the shape. Without it, a twitchy
 * mouse turns an intended move into an unrecoverable delete. A few pixels of
 * hand-shake still counts as a click, so the affordance stays a single click as
 * specified.
 */
export function isVertexDelete(
  pressPx: { readonly x: number; readonly y: number },
  releasePx: { readonly x: number; readonly y: number },
): boolean {
  return Math.hypot(releasePx.x - pressPx.x, releasePx.y - pressPx.y) <= CLICK_SLOP_PX;
}
